import React, { FC } from "react";
import { ContentManager } from "../content-manager";
import { useRef } from "react";
import { useInView } from "@/hooks/reveal-in-view";
import SplitTextAnimation from "../split-text-animation";
import type { RevenueAndProfitsProps } from "@/types/components/p2p-money-abroad/revenue-and-profits";

export const RevenueAndProfits: FC<RevenueAndProfitsProps> = ({
  title,
  values,
  subtitle
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px 25% 0px" });

  const animateNumber = (element: Element, targetValue: number) => {
    let startValue = 0;
    const step = Math.ceil(targetValue / 100); // Increment step for smooth animation
    const duration = 1000; // Animation duration in milliseconds

    const animateValue = () => {
      if (startValue < targetValue) {
        startValue += step;
        const newText = element.innerHTML.replace(
          /\d+/,
          (startValue > targetValue ? targetValue : startValue).toString()
        );
        element.innerHTML = newText;
        requestAnimationFrame(animateValue);
      }
    };

    animateValue();
  };

  const animateNumbersInViewport = () => {
    const parentElement = document.querySelector(".homeWhatYouCanDo--values");
    if (!parentElement) return; // Check if the parent element exists

    const elements = parentElement.querySelectorAll(
      ".homeWhatYouCanDo--value-description"
    );

    const observerCallback: IntersectionObserverCallback = (
      entries,
      observer
    ) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          elements.forEach((element) => {
            const text = element.textContent;
            if (!text) return; // Check if the element's text content exists

            const regex = /\d+/g;
            const matches = text.match(regex);

            if (matches) {
              const targetValue = parseInt(matches[0], 10); // Parse the matched number as an integer
              animateNumber(element, targetValue);
            }
          });

          observer.unobserve(parentElement); // Stop observing once the parent element is in the viewport
        }
      });
    };

    const observerOptions: IntersectionObserverInit = {
      root: null, // Use the viewport as the root element
      threshold: 0.5, // Percentage of the element's visibility required to trigger the callback
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );
    observer.observe(parentElement);
  };

  // Call the animation function when the component is mounted or whenever necessary
  React.useEffect(() => {
    animateNumbersInViewport();
  }, []);

  return (
    <section className="revenueAndProfits">
      <div className="wrapper">
        <SplitTextAnimation
          htmlElement={<h2 className="revenueAndProfits--title" ref={ref}>{title}</h2>}
          isInView={isInView}
        />
      </div>
      <div className="wrapper">
        <div className="revenueAndProfits--values">
          {values.map((value, index) => {
            return (
              <div key={index} className="revenueAndProfits--value">
                {value.title && (
                  <ContentManager
                    content={value.title}
                    className="revenueAndProfits--value-title"
                  />
                )}
                {value.description && (
                  <ContentManager
                    content={value.description}
                    className="revenueAndProfits--value-description"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="wrapper">
        <SplitTextAnimation
          htmlElement={<h2 className="revenueAndProfits--subtitle">{subtitle}</h2>}
          isInView={isInView}
        />
      </div>
    </section>
  );
};
